import { useMemo } from 'react';
import { SOCIAL_NETWORKS, storeLanguage } from '@xshop/shared';
import type { PageType } from '../themes/registry';
import type { StorefrontProduct } from '../api/client';
import type { StoreFacts } from '../elements/render/storeFacts';
import type { CollectionFacts } from './usePageCollection';
import type { ContentFacts } from '../useContentPage';
import { cartUnits, productFactsOf } from './themeMount';
import { contactOf } from '../elements/render/storeContact';

// What a bound prop reads, narrowed out of the shell and held steady across renders.
//
// The steadiness is the reason this is a hook rather than a call. The element tree takes these facts as
// wiring, the way it takes the store's design tokens, so a fresh object on every render would hand the
// whole tree a new context value and redraw every node on every keystroke in the checkout.

/** Everything the shell has resolved that a binding may name, before it is narrowed to the facts. */
export type StoreFactsInput = {
  storeName: string;
  logoUrl: string;
  currency: string;
  direction: 'ltr' | 'rtl';
  page: PageType;
  product: StorefrontProduct;
  images: StorefrontProduct['images'];
  unitPrice: number;
  compareAtPrice: number | null;
  quantity: number;
  /** The collection this page is about, on a collection page; nothing anywhere else. */
  collection?: CollectionFacts | null;
  merchandiseTotal: number;
  /** The store's own settings bag, which names the language it is served in. */
  settings?: unknown;
  /** The page the merchant wrote, on a page reached at /pages/<handle>; nothing anywhere else. */
  content?: ContentFacts;
};

// Reads the store's social profiles that have an address, in the order a row of icons draws them.
function socialOf(settings: unknown): StoreFacts['social'] {
  const social = ((settings as { brand?: { social?: Record<string, unknown> } } | null)?.brand?.social) ?? {};
  return SOCIAL_NETWORKS.flatMap((network) => (typeof social[network] === 'string' && social[network] ? [{ network, url: social[network] as string }] : []));
}

// Reads what the shopper searched for off the address, on the search page only.
const searchFacts = (page: PageType): StoreFacts['search'] =>
  page === 'search' ? { query: (new URLSearchParams(window.location.search).get('q') ?? '').trim().slice(0, 100) } : undefined;

// Narrows the shell to what a bound prop may read, rebuilt only when one of those facts actually moves.
export function useStoreFacts(input: StoreFactsInput): StoreFacts {
  const {
    storeName, logoUrl, currency, direction, page, product, images, unitPrice, compareAtPrice, quantity, merchandiseTotal, collection, content
  } = input;
  const language = storeLanguage(input.settings) ?? undefined;
  const query = searchFacts(page)?.query;
  // Held with the settings it is read from, so the facts are not rebuilt while the settings stand still.
  const social = useMemo(() => socialOf(input.settings), [input.settings]);
  const contact = useMemo(() => (input.settings ? contactOf(input.settings) : undefined), [input.settings]);

  return useMemo<StoreFacts>(() => ({
    storeName,
    logoUrl,
    currency,
    moneyLocale: direction === 'rtl' ? 'ar-MA' : 'fr-MA',
    cartCount: cartUnits(product, quantity, page),
    cartTotal: merchandiseTotal,
    product: productFactsOf(product, { unitPrice, compareAtPrice }, images),
    ...(collection ? { collection } : {}),
    ...(content ? { page: content } : {}),
    ...(query !== undefined ? { search: { query } } : {}),
    social,
    ...(contact ? { contact } : {}),
    ...(language ? { language } : {}),
    direction
  }), [storeName, logoUrl, currency, direction, page, product, images, unitPrice, compareAtPrice, quantity, merchandiseTotal, collection, language, content, query, social, contact]);
}
